import React, { useEffect, useState, useMemo } from "react";
import { PropTypes } from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { Formik, Form, Field, useField, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCourseEnrollments,
  updateCourseEnrollments,
  updateCourse
} from "../../../../redux/actions/contentCoursesActions";
import { useTranslation } from "react-i18next";
import { trainingObjectStatusIds } from "../../../../constants";
import { isFutureDate } from "../../../../utils/datetime";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import Table from "../../../../components/Table/Table";
import { push } from "connected-react-router";
import { buildCourseDetailsSessionsRoute } from "../../../../routes/AdminRoutes";
import { CheckboxWithLabel } from "formik-mui";
import AdminScoreCell from "../../../../components/ScoreCell/AdminScoreCell";
import CourseSessionRosterButtons from "./CourseSessionRosterButtons";
import RadioGroup from "../../../../components/FormikFields/RadioGroup";
import useLoadingState from "../../../../hooks/useLoadingState";
import WarningModal from "../../../../components/WarningModal/WarningModal";

const AttendanceCell = ({ rowData, disabled }) => {
  const { t } = useTranslation();
  const [field, , { setValue }] = useField(
    `enrollments[${rowData.tableData.id}].attendance`
  );

  const handleClick = ({ target: { value } }) => {
    if (value === `${field.value}`) {
      setValue("");
    } else {
      setValue(value);
    }
  };

  return (
    <RadioGroup
      name={field.name}
      radioButtons={[
        {
          label: (
            <Typography variant="body2">
              {t("courseSession.attendanceYesLabel")}
            </Typography>
          ),
          value: "true"
        },
        {
          label: (
            <Typography variant="body2">
              {t("courseSession.attendanceNoLabel")}
            </Typography>
          ),
          value: "false"
        }
      ]}
      noWrap
      onClick={handleClick}
      disabled={disabled}
    />
  );
};

const UnenrollCell = ({ disabled, rowIndex }) => {
  const { t } = useTranslation();

  return (
    <Field
      component={CheckboxWithLabel}
      name={`enrollments[${rowIndex}].unenroll`}
      Label={{
        label: (
          <Typography variant="body2">
            {t("courseSession.unenrollLabel")}
          </Typography>
        )
      }}
      color="primary"
      disabled={disabled}
      type="checkbox"
    />
  );
};

const CourseSessionAttendanceTable = ({
  futureIlt,
  paperBasedTest,
  enrollments,
  isLoading,
  isSaving,
  isClosedOut,
  onSave,
  onCloseOut,
  showCloseOutBtn,
  sessionId
}) => {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext();
  const [attendanceHeaderChecked, setAttendanceHeaderChecked] = useState(false);

  useEffect(() => {
    if (attendanceHeaderChecked) {
      const newEnrollmentsData = values.enrollments.map(enrollment => ({
        ...enrollment,
        attendance: "true"
      }));
      setFieldValue("enrollments", newEnrollmentsData);
    }
  }, [attendanceHeaderChecked]);

  const attendanceColumn = {
    title: "attendance",
    field: "attendance",
    cellStyle: {
      minWidth: 160,
      paddingLeft: 14
    },
    headerCheckboxProps: {
      onCheck: setAttendanceHeaderChecked,
      checked: attendanceHeaderChecked,
      disabled: isClosedOut
    },
    render: rowData => (
      <AttendanceCell rowData={rowData} disabled={isClosedOut} />
    )
  };

  const scoreColumn = {
    title: "score",
    field: "score",
    cellStyle: {
      minWidth: 120
    },
    render: rowData => (
      <AdminScoreCell
        rowIndex={rowData.tableData.id}
        scoreData={rowData.score}
        postScore={rowData.postScore}
        isEditable={paperBasedTest && !isClosedOut}
      />
    )
  };
  const getCoursesTableColumns = () => {
    let visibleColumns = [
      {
        title: "Name",
        field: "lastName",
        cellStyle: {
          minWidth: 120
        },
        options: {
          sorting: true
        },
        render: ({ fullName }) => fullName
      },
      {
        title: "Location",
        field: "location",
        cellStyle: {
          minWidth: 120
        }
      },
      {
        title: "unenrollEmployee",
        cellStyle: {
          minWidth: 160
        },
        render: rowData => (
          <UnenrollCell
            disabled={isClosedOut}
            rowIndex={rowData.tableData.id}
          />
        )
      }
    ];
    // we only display the the score & attendance columns if the session date has already passed
    if (!futureIlt) {
      visibleColumns.splice(2, 0, attendanceColumn);
      visibleColumns.splice(4, 0, scoreColumn);
    }
    return visibleColumns;
  };

  const table = useMemo(() => {
    return (
      <Table
        columns={getCoursesTableColumns(t)}
        data={enrollments}
        isLoading={isLoading || isSaving}
        padding="dense"
        hidePagination
      />
    );
  }, [enrollments, attendanceHeaderChecked]);

  return (
    <Form>
      {table}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Box>
          <CourseSessionRosterButtons sessionId={sessionId} />
        </Box>
        <Box>
          <ButtonGroup>
            {showCloseOutBtn && (
              <Button
                color="primary"
                variant="contained"
                disabled={isClosedOut || isLoading || isSaving}
                onClick={() => onCloseOut()}
                data-qa="course-session-attendance-close-out-btn"
              >
                {t("courseSession.closeOutBtn")}
              </Button>
            )}
            <Button
              color="primary"
              variant="contained"
              disabled={isClosedOut || isLoading || isSaving}
              onClick={() => onSave()}
              data-qa="course-session-attendance-save-btn"
            >
              {t("courseSession.saveChangesBtn")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Form>
  );
};

const InactivateNoticeModal = ({ open, onClose, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <WarningModal
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      showCloseIcon={false}
      warningMessage={t("courseSession.inactivateCourseWarningMessage")}
    />
  );
};

const CourseSessionAttendance = ({ sessionData, courseId }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector(
    ({
      contentCoursesState: { isFetchingEnrollments, isUpdatingEnrollments }
    }) => ({
      loading: isFetchingEnrollments
    })
  );
  const isSaving = useLoadingState([
    "UPDATE_COURSE_ENROLLMENTS",
    "UPDATE_COURSE"
  ]);
  const [enrollmentsTableData, setEnrollmentsTableData] = useState([]);
  const [inactivateNoticeModalOpen, setInactivateNoticeModalOpen] = useState(
    false
  );
  useEffect(() => {
    if (sessionData) {
      dispatch(fetchCourseEnrollments(`${sessionData.id}`));
    }
  }, []);

  useEffect(() => {
    if (sessionData.enrollments && !loading) {
      const newEnrollmentsData = sessionData.enrollments.map(enrollment => ({
        ...enrollment,
        postScore: Number.isInteger(enrollment.postScore)
          ? `${enrollment.postScore}`
          : "",
        unenroll: false,
        attendance: `${enrollment.attended}`
      }));
      setEnrollmentsTableData(newEnrollmentsData);
    }
  }, [sessionData.enrollments]);

  const handleSaveEnrollments = ({ enrollments }) => {
    const enrollmentsData = enrollments.map(
      ({ type, id, unenroll, attendance, postScore }) => ({
        type,
        id,
        unenroll,
        attendance,
        postScore
      })
    );
    return dispatch(
      updateCourseEnrollments(`${sessionData.id}`, enrollmentsData)
    );
  };

  const handleInactivate = () => {
    dispatch(
      updateCourse(sessionData.id, {
        ...sessionData,
        statusTypeId: trainingObjectStatusIds.inactive
      })
    ).then(() => dispatch(push(buildCourseDetailsSessionsRoute(courseId))));
  };

  const handleCloseOut = newEnrollmentsData => {
    if (newEnrollmentsData && newEnrollmentsData.enrollments.length === 0) {
      setInactivateNoticeModalOpen(true);
    } else {
      handleSaveEnrollments(newEnrollmentsData).then(() => {
        dispatch(
          updateCourse(sessionData.id, {
            ...sessionData,
            statusTypeId: trainingObjectStatusIds.sessionClosedOut
          })
        ).then(() => dispatch(push(buildCourseDetailsSessionsRoute(courseId))));
      });
    }
  };

  const isClosedOut =
    sessionData.statusTypeId === trainingObjectStatusIds.sessionClosedOut;
  const isInactive =
    sessionData.statusTypeId === trainingObjectStatusIds.inactive;

  const isFutureIlt = isFutureDate(sessionData.startDate);

  /**
   * Only show the close out button if the course has been closed out
   * or attendance has been saved for every enrollment. If the session uses
   * paper based test, only show when the score has been marked for every user that has
   * attendance = true
   **/
  const showCloseOutBtn =
    isClosedOut ||
    (!isFutureIlt &&
      !loading &&
      sessionData.enrollments &&
      !sessionData.enrollments.find(({ attended }) => attended == null) &&
      (!sessionData.paperBasedTest ||
        sessionData.enrollments.every(
          ({ postScore, attended }) =>
            attended === false ||
            (typeof postScore === "number" && postScore >= 0)
        )));

  return (
    <Box
      display="flex"
      flexDirection="column"
      mt={4}
      flex={1}
      justifyContent="space-between"
      data-qa="course-session-attendance-table-container"
    >
      <InactivateNoticeModal
        open={inactivateNoticeModalOpen}
        onClose={() => setInactivateNoticeModalOpen(false)}
        onSubmit={handleInactivate}
      />
      <Formik
        initialValues={{ enrollments: enrollmentsTableData }}
        enableReinitialize
      >
        {({ values }) => (
          <CourseSessionAttendanceTable
            futureIlt={isFutureIlt}
            enrollments={enrollmentsTableData}
            isLoading={loading}
            isSaving={isSaving}
            isClosedOut={isClosedOut || isInactive}
            showCloseOutBtn={showCloseOutBtn}
            onCloseOut={() => handleCloseOut(values)}
            onSave={() => handleSaveEnrollments(values)}
            paperBasedTest={sessionData.paperBasedTest}
            sessionId={sessionData.id}
          />
        )}
      </Formik>
    </Box>
  );
};

CourseSessionAttendance.propTypes = {
  sessionData: PropTypes.shape({
    id: PropTypes.string,
    statusTypeId: PropTypes.string
  }),
  courseId: PropTypes.string
};

export default CourseSessionAttendance;
